<template>
    <v-form ref="form" class="partner-ship">
        <div class="partner-ship__location-section">
            <DropdownPartnerLocations
                ref="partnerDropdown"
                v-model="location"
                class="partner-ship__location-section__location"
                :rules="[ rules.requiredObject ]"
            />
            <ClDatePicker
                ref="datePicker"
                v-model="expectedReceiveDate"
                class="partner-ship__date-picker"
                label="Expected Receive Date"
                :min="DateUtil.formatDate( Date.now() )"
                :rules="[ rules.required ]"
                @blur="validateDatePicker"
            />
        </div>
        <v-divider />
        <div class="partner-ship__scan-section">
            <v-row class="scan-container">
                <v-col
                    cols="6"
                    class="scan-input"
                >
                    <ClInput
                        v-model="scan"
                        type="text"
                        autofocus
                        placeholder="Scan a Product"
                        @keyup.enter="verifyAndProcessScan()"
                    />
                </v-col>
                <v-col
                    cols="6"
                    class="scan-count"
                >
                    <p>Scanned Items:</p>
                    <p class="scan-count__counter" >{{ scannedItems.length }}</p>
                </v-col>
            </v-row>
            <v-row class="table-container">
                <v-col cols="12">
                    <ClTableSimple
                        class="shipping-table"
                        :items="scannedItems"
                        :headers="headers"
                        height="50vh"
                    >
                        <template #[`item.remove`]="{ item }">
                            <ClButton
                                icon
                                aria-label="Remove Item"
                                @click="removeScannedItem( item )"
                            >
                                <v-icon>mdi-delete-outline</v-icon>
                            </ClButton>
                        </template>
                        <template #[`item.status`]="{ item }">
                            <ItemStatus :item="item" />
                        </template>
                    </ClTableSimple>
                </v-col>
            </v-row>
            <v-row class="scan-section__row">
                <v-spacer/>
                <v-col cols="auto">
                    <ClButton
                        type="secondary"
                        @click="cancelButtonHandler()"
                    >
                        Cancel
                    </ClButton>
                </v-col>
                <v-col cols="auto">
                    <ClButton
                        type="primary"
                        :disabled="disableSave"
                        :loading="inFlight"
                        @click="saveButtonHandler()"
                    >
                        Done
                    </ClButton>
                </v-col>
            </v-row>
        </div>
        <ClConfirmationModal
            v-model="showConfirmCancelModal"
            titleText="Cancel"
            primaryActionText="Yes"
            secondaryActionText="No"
            :roundButton="false"
            @cancel="showConfirmCancelModal = false"
            @confirm="cancel()"
        >
            <template #content>
                Are you sure you want to cancel?
            </template>
        </ClConfirmationModal>
        <ClConfirmationModal
            v-model="showConfirmDoneModal"
            titleText="Please Acknowledge"
            primaryActionText="Proceed"
            secondaryActionText="Cancel"
            :roundButton="false"
            @cancel="showConfirmDoneModal = false"
            @confirm="save()"
        >
            <template #content>
                {{ scannedItems.length }} items will be Partner Shipped to <b>{{ destinationLocation.friendly }}</b>
            </template>
        </ClConfirmationModal>
    </v-form>
</template>

<script>
// Vuex
import {
    mapActions, mapMutations, mapState, mapGetters,
} from 'vuex';

// Components
import DropdownPartnerLocations from '@/components/DropdownPartnerLocations';
import ItemStatus from '@/views/shared/ItemStatus';

// Utils
import DateUtil from '@/utils/DateUtil';
import ValidationUtil from '@/utils/ValidationUtil';

export default {
    name: 'PartnerShip',
    components: {
        DropdownPartnerLocations,
        ItemStatus,
    },
    data() {
        return {
            DateUtil,
            rules: {
                ...ValidationUtil,
            },
            headers: [
                {
                    text: 'Item ID',
                    value: 'serial',
                },
                {
                    text: 'Title',
                    value: 'title',
                },
                {
                    text: 'ISBN',
                    value: 'isbn',
                },
                {
                    text: 'Status',
                    value: 'status',
                },
                {
                    text: '',
                    value: 'remove',
                    sortable: false,
                    width: '75px',
                },
            ],
            showConfirmCancelModal: false,
            showConfirmDoneModal: false,
        };
    },
    computed: {
        ...mapState( {
            scannedItems: ( state ) => state.Inventory.PartnerShip.scannedItems,
            scanString: ( state ) => state.Inventory.PartnerShip.scanString,
            destinationLocation: ( state ) => state.Inventory.PartnerShip.destinationLocation,
            inFlight: ( state ) => state.Inventory.PartnerShip.inFlight,
        } ),
        ...mapGetters( {
            hasInfo: 'Website/hasInfo',
        } ),
        scan: {
            get() {
                return this.scanString;
            },
            set( val ) {
                this.setScanString( { scanString: val } );
            },
        },
        location: {
            get() {
                return this.destinationLocation;
            },
            set( val ) {
                this.setDestinationLocation( { destinationLocation: val } );
            },
        },
        expectedReceiveDate: {
            get() {
                return this.$store.state.Inventory.PartnerShip.expectedReceiveDate;
            },
            set( val ) {
                this.$store.commit( 'Inventory/PartnerShip/SET_EXPECTED_RECEIVE_DATE', { expectedReceiveDate: val } );
            },
        },
        /** @returns { Boolean } */
        disableSave() {
            return this.inFlight
                || !this.hasInfo
                || !this.expectedReceiveDate
                || !this.destinationLocation?.id;
        },
    },
    beforeDestroy() {
        this.$store.dispatch( 'Inventory/PartnerShip/reset' );
    },
    methods: {
        ...mapMutations( {
            setScanString: 'Inventory/PartnerShip/SET_SCAN_STRING',
            removeScannedItem: 'Inventory/PartnerShip/REMOVE_SCANNED_ITEM',
            setDestinationLocation: 'Inventory/PartnerShip/SET_DESTINATION_LOCATION',
            resetScannedItems: 'Inventory/PartnerShip/RESET_SCANNED_ITEMS',
        } ),
        ...mapActions( {
            completePartnerShip: 'Inventory/PartnerShip/completePartnerShip',
            verifyAndProcessScan: 'Inventory/PartnerShip/verifyAndProcessScan',
        } ),
        validateDatePicker() {
            // The first param is a boolean which tells
            // the field to force run the validation when true
            this.$refs.datePicker.validate( true );
        },
        cancelButtonHandler() {
            this.showConfirmCancelModal = true;
        },
        cancel() {
            this.$refs.form.reset();
            this.$nextTick( () => {
                this.expectedReceiveDate = DateUtil.formatDate( Date.now() );
            } );
            this.resetScannedItems();
        },
        saveButtonHandler() {
            this.showConfirmDoneModal = true;
        },
        async save() {
            if ( await this.completePartnerShip() ) {
                this.resetScannedItems();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .partner-ship {
        &__location-section {
            display: grid;
            grid-template-columns: 1fr;
            row-gap: 2rem;
            column-gap: 2rem;
            justify-content: space-between;

            margin: 2rem 0;

            .date-container {
                min-width: 275px;
            }
            @include mixins-above(tablet) {
                grid-template-columns: 50% max-content;
            }
        }
        &__scan-section {
            margin: 2rem 0;
            align-items: center;

            .scan-container {
                .scan-count {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    p {
                        margin: 0
                    }
                    &__counter {
                        font-size: 2rem;
                        padding-left: .5rem;
                    }
                }
            }
            .table-container {
                .shipping-table {
                    border: 1px solid rgba( 0, 0, 0, .38 );
                    overflow-y: auto;
                    &:hover {
                        border: 1px solid rgba( 0, 0, 0, .87 );
                    }
                }
            }
        }
    }
</style>
